import type { Account, AppData, AppSettings, BalanceAdjustment, Transfer } from "../domain/types";
import { createDefaultAccounts } from "../domain/defaultAccounts";

const DB_NAME = "money-ipoget";
const DB_VERSION = 1;
const STORES = ["accounts", "transfers", "adjustments", "settings"] as const;
const defaults: AppSettings = { id: "settings", schemaVersion: 1, warningDays: 3, backupWarningDays: 7 };

const request = <T>(r: IDBRequest<T>) => new Promise<T>((resolve, reject) => { r.onsuccess = () => resolve(r.result); r.onerror = () => reject(r.error); });
const done = (tx: IDBTransaction) => new Promise<void>((resolve, reject) => { tx.oncomplete = () => resolve(); tx.onerror = () => reject(tx.error); tx.onabort = () => reject(tx.error ?? new Error("データ保存を中止しました")); });

export function openDatabase() {
  return new Promise<IDBDatabase>((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains("accounts")) db.createObjectStore("accounts", { keyPath: "id" });
      if (!db.objectStoreNames.contains("transfers")) db.createObjectStore("transfers", { keyPath: "id" });
      if (!db.objectStoreNames.contains("adjustments")) db.createObjectStore("adjustments", { keyPath: "id" });
      if (!db.objectStoreNames.contains("settings")) db.createObjectStore("settings", { keyPath: "id" });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function loadData(): Promise<AppData> {
  const db = await openDatabase(); const tx = db.transaction(STORES, "readonly");
  const [accounts, transfers, adjustments, settings] = await Promise.all([
    request(tx.objectStore("accounts").getAll()) as Promise<Account[]>,
    request(tx.objectStore("transfers").getAll()) as Promise<Transfer[]>,
    request(tx.objectStore("adjustments").getAll()) as Promise<BalanceAdjustment[]>,
    request(tx.objectStore("settings").get("settings")) as Promise<AppSettings | undefined>,
  ]);
  await done(tx); db.close();
  if (accounts.length === 0 && transfers.length === 0 && adjustments.length === 0 && !settings) {
    const initial: AppData = { schemaVersion: 1, accounts: createDefaultAccounts(), transfers: [], adjustments: [], settings: defaults };
    await replaceAllData(initial);
    return initial;
  }
  return { schemaVersion: 1, accounts, transfers, adjustments, settings: settings ?? defaults };
}

async function put<T>(storeName: typeof STORES[number], value: T) {
  const db = await openDatabase(); const tx = db.transaction(storeName, "readwrite"); tx.objectStore(storeName).put(value); await done(tx); db.close();
}
export const saveAccount = (value: Account) => put("accounts", value);
export const saveTransfer = (value: Transfer) => put("transfers", value);
export const saveAdjustment = (value: BalanceAdjustment) => put("adjustments", value);
export const saveSettings = (value: AppSettings) => put("settings", value);

export async function replaceAllData(data: AppData) {
  const db = await openDatabase(); const tx = db.transaction(STORES, "readwrite");
  for (const name of STORES) tx.objectStore(name).clear();
  data.accounts.forEach((a) => tx.objectStore("accounts").put(a));
  data.transfers.forEach((t) => tx.objectStore("transfers").put(t));
  data.adjustments.forEach((a) => tx.objectStore("adjustments").put(a));
  tx.objectStore("settings").put(data.settings);
  await done(tx); db.close();
}

export async function clearAllData() {
  await replaceAllData({ schemaVersion: 1, accounts: createDefaultAccounts(), transfers: [], adjustments: [], settings: defaults });
}
